import { BaseDomainEvent } from './DomainEvent';
import { EventSource } from './EventTypes';

/**
 * Domain event emitted when a new registration creation is requested.
 */
export class RegistrationCreateRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'REGISTRATION_CREATE_REQUESTED';

    constructor(
        primaryImageSeriesUid: string,
        secondaryImageSeriesUid: string,
        source: EventSource = 'user'
    ) {
        super(
            RegistrationCreateRequestedEvent.EVENT_TYPE,
            `registration-create-${secondaryImageSeriesUid}`,
            { primaryImageSeriesUid, secondaryImageSeriesUid, source }
        );
    }

    get primaryImageSeriesUid(): string {
        return this.data.primaryImageSeriesUid;
    }

    get secondaryImageSeriesUid(): string {
        return this.data.secondaryImageSeriesUid;
    }

    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when a registration deletion is requested.
 */
export class RegistrationDeleteRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'REGISTRATION_DELETE_REQUESTED';

    constructor(
        registrationSopInstanceUid: string,
        source: EventSource = 'user'
    ) {
        super(
            RegistrationDeleteRequestedEvent.EVENT_TYPE,
            `registration-${registrationSopInstanceUid}`,
            { registrationSopInstanceUid, source }
        );
    }

    get registrationSopInstanceUid(): string {
        return this.data.registrationSopInstanceUid;
    }

    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when a registration rename is requested.
 */
export class RegistrationRenameRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'REGISTRATION_RENAME_REQUESTED';

    constructor(
        registrationSopInstanceUid: string,
        newLabel: string,
        source: EventSource = 'user'
    ) {
        super(
            RegistrationRenameRequestedEvent.EVENT_TYPE,
            `registration-${registrationSopInstanceUid}`,
            {
                registrationSopInstanceUid,
                newLabel,
                source,
            }
        );
    }

    get registrationSopInstanceUid(): string {
        return this.data.registrationSopInstanceUid;
    }

    get newLabel(): string {
        return this.data.newLabel;
    }

    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when a registration approval status
 * change is requested.
 */
export class RegistrationApprovalStatusChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE =
        'REGISTRATION_APPROVAL_STATUS_CHANGE_REQUESTED';

    constructor(
        registrationSopInstanceUid: string,
        newApprovalStatus: string,
        source: EventSource = 'user'
    ) {
        super(
            RegistrationApprovalStatusChangeRequestedEvent.EVENT_TYPE,
            `registration-${registrationSopInstanceUid}`,
            { registrationSopInstanceUid, newApprovalStatus, source }
        );
    }

    get registrationSopInstanceUid(): string {
        return this.data.registrationSopInstanceUid;
    }

    get newApprovalStatus(): string {
        return this.data.newApprovalStatus;
    }

    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when a registration matrix edit has been committed
 * into the DICOM dataset ({@link RigidRegistration.syncTransformationToDataset}
 * reported an actual value change). This is the single funnel for matrix
 * dirty-tracking: manual tools commit on mouse release / keypress, elastix
 * and undo/redo commit through the same sync call — no per-tool wiring, so a
 * new registration tool cannot silently bypass dirty tracking.
 *
 * Carries only the session-local domain id (a UUID minted at load) — never
 * the SOP Instance UID or label, so the event is PHI-safe to log.
 */
export class RegistrationMatrixCommittedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'REGISTRATION_MATRIX_COMMITTED';

    constructor(registrationId: string) {
        super(
            RegistrationMatrixCommittedEvent.EVENT_TYPE,
            `registration-${registrationId}`,
            { registrationId }
        );
    }

    get registrationId(): string {
        return this.data.registrationId;
    }
}

/**
 * Domain event emitted when a registration inversion is requested.
 * Creates a new registration with swapped source/target and inverted
 * transformation, then swaps primary and secondary images in the viewport.
 */
export class RegistrationInvertRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'REGISTRATION_INVERT_REQUESTED';

    constructor(
        registrationSopInstanceUid: string,
        source: EventSource = 'user'
    ) {
        super(
            RegistrationInvertRequestedEvent.EVENT_TYPE,
            `registration-${registrationSopInstanceUid}`,
            { registrationSopInstanceUid, source }
        );
    }

    get registrationSopInstanceUid(): string {
        return this.data.registrationSopInstanceUid;
    }

    get source(): EventSource {
        return this.data.source;
    }
}
